/**
 * The approval queue.
 *
 * This is the screen ops lives in once a brand is live, and the one place in
 * the console where a click has a financial consequence. Everything on it is
 * arranged around one idea: **the operator must know what will happen before
 * they click, not after.**
 *
 *  - The dry-run state is a banner AND part of the button label. A toast
 *    afterwards is too late.
 *  - Rejecting demands a reason. "We decided not to" is what somebody reading
 *    this ticket in three months needs to find, and it only exists if it is
 *    asked for now.
 *    A held refund or replacement picks one from a list (2026-10-27): the
 *    standard ones Zoye answers itself, the rest go to Tier 2.
 *  - `cancel_order` renders as an instruction, not a disabled button with no
 *    explanation: the adapter cannot do it, so the operator does it by hand.
 *  - An empty queue looks empty, and says why an empty queue is expected today.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, MessageCircle, ShieldAlert, TriangleAlert, Wrench } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { DateFilter, FilterSelect, Pager } from '../components/ui/ListControls';
import { useBrands } from '../api/admin';
import { pageFromParams, rangeFromParams, rangeQuery, toQuery, withFilters } from '../lib/filters/list-params';
import { cn } from '../lib/cn';
import { formatRelative, formatUsd, withBillingLinkStandIn } from '../lib/format';
import {
  approveConsequence,
  approveLabel,
  outstandingCents,
  rejectionReasonsFor,
  toApprovalRows,
  type ActionStatus,
  type ApprovalRow,
  type ApprovalsResponse,
  type OrderTags,
  type RejectionReasonOption,
  TYPE_FILTER_OPTIONS,
} from '../lib/approvals/rows';
import { intentLabel } from '../lib/inbox/rows';
import { isSuperadmin, useSession } from '../auth/session';
import { RecheckPanel } from './approvals/RecheckPanel';
import { TikTokQueue } from './approvals/TikTokQueue';
import { useTikTokWaitingCount } from '../api/tickets';

const STATUS_TONE: Record<ActionStatus, 'fact' | 'held' | 'info' | 'stop' | 'neutral'> = {
  proposed: 'held',
  awaiting_approval: 'held',
  approved: 'info',
  executing: 'info',
  confirmed: 'fact',
  failed: 'stop',
  rejected: 'neutral',
  blocked: 'stop',
};

/**
 * Not an action status: the blocked rows that wait only for the customer's
 * yes, which the nightly re-check moves out of Blocked by policy (Deivydas
 * 2026-11-03).
 */
const AWAITING_CUSTOMER_TAB = 'awaiting_customer';

/** The lists a person clears with "Mark handled". */
const HANDLEABLE_QUEUES: ReadonlySet<string> = new Set(['failed', 'blocked', AWAITING_CUSTOMER_TAB]);

/** Not an action status: TikTok chat replies waiting for a person's Send. */
const TIKTOK_TAB = 'tiktok';

const QUEUES: Array<{ key: string; label: string }> = [
  { key: 'awaiting_approval', label: 'Waiting on you' },
  // The TikTok approval week (Deivydas 2026-10-26): replies, not actions.
  { key: TIKTOK_TAB, label: 'TikTok' },
  // Auto mode files its writes here pre-vetted, and a dry-run Execute
  // returns a row here — without this tab that work was invisible and
  // could never be executed (pre-pilot QA #2).
  { key: 'approved', label: 'Pre-approved' },
  { key: 'proposed', label: 'Proposed' },
  { key: 'blocked', label: 'Blocked by policy' },
  { key: AWAITING_CUSTOMER_TAB, label: 'Awaiting customer' },
  { key: 'confirmed', label: 'Done' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'failed', label: 'Failed' },
];

const DECIDED_BY_OPTIONS = [
  { value: 'zoye', label: 'Zoye (automatic)' },
  { value: 'person', label: 'A person' },
];

const TAG_OPTIONS = [
  { value: 'pending', label: 'Tags pending' },
  { value: 'written', label: 'Tags written' },
  { value: 'person', label: "Person's to stamp" },
  { value: 'none', label: 'Nothing planned' },
  { value: 'problem', label: 'Failed / skipped / dry run' },
];

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const brands = useBrands();
  const session = useSession();
  const superadmin = session.data ? isSuperadmin(session.data) : false;
  // Every filter lives in the URL (Deivydas 2026-10-14): tab, date, brand,
  // action type, who decided, order tags (Done), search, page.
  const [params, setParams] = useSearchParams();
  const set = (patch: Record<string, string | null>) => setParams((current) => withFilters(current, patch));
  const status = params.get('tab') ?? 'awaiting_approval';
  const [openRow, setOpenRow] = useState<string | null>(null);
  // Failed and Blocked list what still needs someone; "Show handled" lists
  // the rows a person already dealt with (Deivydas 2026-10-14).
  const showHandled = params.get('handled') === '1';
  const handleable = HANDLEABLE_QUEUES.has(status);
  const listingHandled = handleable && showHandled;
  const range = rangeFromParams(params);
  const brandId = params.get('brand') ?? '';
  const type = params.get('type') ?? '';
  const decidedBy = params.get('by') ?? '';
  const tags = status === 'confirmed' ? (params.get('tags') ?? '') : '';
  const q = params.get('q') ?? '';
  const page = pageFromParams(params);
  // The search box types freely; the URL (and the query) follow a beat later.
  const [searchDraft, setSearchDraft] = useState(q);
  useEffect(() => setSearchDraft(q), [q]);
  useEffect(() => {
    if (searchDraft.trim() === q) return;
    const timer = setTimeout(() => set({ q: searchDraft.trim() || null }), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const query = toQuery({
    status,
    handled: listingHandled ? '1' : null,
    brandId,
    type,
    decidedBy,
    tags,
    q,
    page,
    ...rangeQuery(range),
  });
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['approvals', query],
    queryFn: () => apiGet<ApprovalsResponse>(`/api/approvals?${query}`),
    // The table stays on screen while the next page or filter loads.
    placeholderData: keepPreviousData,
  });
  const tiktokCount = useTikTokWaitingCount();
  const onTikTok = status === TIKTOK_TAB;
  const filtered = brandId !== '' || type !== '' || decidedBy !== '' || tags !== '' || q !== '';
  const brandOptions = useMemo(
    () => (brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
    [brands.data],
  );

  const rows = useMemo(() => toApprovalRows(data?.actions ?? []), [data]);
  const dryRun = data?.dryRun ?? true;
  const outstanding = outstandingCents(rows);
  // The tags column answers "did the order get its tags?" — a question only
  // finished actions have (Deivydas 2026-10-12).
  const showTags = status === 'confirmed';

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['approvals'] });

  if (isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-stop-700" aria-hidden />
        <p className="text-sm text-ink-700">
          {error instanceof Error ? error.message : 'Could not load the approval queue.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      {onTikTok ? <TikTokBanner /> : dryRun ? <DryRunBanner /> : <LiveBanner />}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {QUEUES.map((queue) => (
          <button
            key={queue.key}
            type="button"
            onClick={() =>
              set({ tab: queue.key === 'awaiting_approval' ? null : queue.key, handled: null, tags: null })
            }
            aria-pressed={status === queue.key}
            className={cn(
              'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors duration-fast',
              status === queue.key
                ? 'bg-ink-900 text-paper'
                : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
            )}
          >
            {queue.label}
            {(queue.key === TIKTOK_TAB ? tiktokCount.data !== undefined : data?.counts) ? (
              <span
                className={cn('tabular ml-1.5 text-2xs', status === queue.key ? 'text-paper/70' : 'text-ink-400')}
              >
                {(queue.key === TIKTOK_TAB ? (tiktokCount.data ?? 0) : (data?.counts[queue.key] ?? 0)).toLocaleString(
                  'en-US',
                )}
              </span>
            ) : null}
          </button>
        ))}

        {handleable ? (
          <label className="ml-2 flex items-center gap-1.5 text-xs text-ink-500">
            <input
              type="checkbox"
              checked={showHandled}
              onChange={(event) => set({ handled: event.target.checked ? '1' : null })}
            />
            Show handled
          </label>
        ) : null}

        {outstanding > 0 && !onTikTok ? (
          <span className="ml-auto text-xs text-ink-500">
            <span className="tabular font-semibold text-ink-900">{formatUsd(outstanding)}</span>{' '}
            waiting on a decision
          </span>
        ) : null}
      </div>

      {onTikTok ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Brand"
              allLabel="All brands"
              value={brandId}
              onChange={(value) => set({ brand: value })}
              options={brandOptions}
            />
          </div>
          <TikTokQueue brandId={brandId} page={page} onPage={(next) => set({ page: next > 1 ? String(next) : null })} />
        </>
      ) : (
      <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <DateFilter
          range={range}
          onChange={(next) =>
            set(next ? { range: null, from: next.from, to: next.to } : { range: 'all', from: null, to: null })
          }
        />
        <FilterSelect
          label="Brand"
          allLabel="All brands"
          value={brandId}
          onChange={(value) => set({ brand: value })}
          options={brandOptions}
        />
        <FilterSelect
          label="Action type"
          allLabel="All actions"
          value={type}
          onChange={(value) => set({ type: value })}
          options={TYPE_FILTER_OPTIONS}
        />
        <FilterSelect
          label="Decided by"
          allLabel="Decided by anyone"
          value={decidedBy}
          onChange={(value) => set({ by: value })}
          options={DECIDED_BY_OPTIONS}
        />
        {status === 'confirmed' ? (
          <FilterSelect
            label="Order tags"
            allLabel="Any order tags"
            value={tags}
            onChange={(value) => set({ tags: value })}
            options={TAG_OPTIONS}
          />
        ) : null}
        <input
          type="search"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          placeholder="Customer, email or ticket #"
          aria-label="Search customer, email or ticket number"
          className="h-7 w-56 rounded-sm border border-line bg-surface px-2 text-xs text-ink-900"
        />
        {filtered ? (
          <button
            type="button"
            onClick={() => set({ brand: null, type: null, by: null, tags: null, q: null })}
            className="text-xs text-ink-400 underline hover:text-ink-900"
          >
            clear filters
          </button>
        ) : null}
        {isFetching ? <span className="text-2xs text-ink-400">updating…</span> : null}
      </div>

      {superadmin && (status === 'blocked' || status === AWAITING_CUSTOMER_TAB) && !showHandled ? (
        <RecheckPanel onDone={refresh} awaitingCustomer={status === AWAITING_CUSTOMER_TAB} />
      ) : null}

      {rows.length === 0 ? (
        filtered ? (
          <p className="px-5 py-12 text-center text-sm text-ink-400">Nothing matches these filters.</p>
        ) : listingHandled ? (
          <p className="px-5 py-12 text-center text-sm text-ink-400">Nothing has been marked handled yet.</p>
        ) : (
          <EmptyQueue status={status} />
        )
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Action</Th>
                <Th>Customer</Th>
                <Th>Brand</Th>
                <Th>Order</Th>
                {showTags ? <Th>Order tags</Th> : null}
                <Th>Status</Th>
                <Th>Raised</Th>
                <Th className="text-right">Decision</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ActionRow
                  key={row.id}
                  row={row}
                  dryRun={dryRun}
                  showTags={showTags}
                  refundReasons={data?.refundReasons ?? []}
                  rejectionReasons={data?.rejectionReasons ?? []}
                  open={openRow === row.id}
                  onToggle={() => setOpenRow(openRow === row.id ? null : row.id)}
                  onDone={refresh}
                />
              ))}
            </tbody>
          </Table>
          {data && data.total > 0 ? (
            <Pager
              className="border-t border-line px-4 py-2"
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onPage={(next) => set({ page: next > 1 ? String(next) : null })}
            />
          ) : null}
        </Card>
      )}
      </>
      )}
    </div>
  );
}

function ActionRow({
  row,
  dryRun,
  showTags,
  refundReasons,
  rejectionReasons,
  open,
  onToggle,
  onDone,
}: {
  row: ApprovalRow;
  dryRun: boolean;
  showTags: boolean;
  refundReasons: string[];
  rejectionReasons: RejectionReasonOption[];
  open: boolean;
  onToggle: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  // Why a held refund or replacement is rejected (ops proposal, 2026-10-27):
  // a standard reason Zoye answers itself, or one that goes to Tier 2.
  const rejectionChoices = rejectionReasonsFor(row.type, rejectionReasons);
  // R1 (handoff review 2026-10-29): a rejection the customer hears about by
  // default; "Other" stays one click away.
  const [rejection, setRejection] = useState(() =>
    rejectionChoices.some((choice) => choice.value === 'tell_customer') ? 'tell_customer' : '',
  );
  const picked = rejectionChoices.find((choice) => choice.value === rejection) ?? null;
  // "Other", or a row with no reasons to pick, still needs the words — and
  // "Zoye tells the customer" needs the words the customer reads (2026-10-28).
  const forCustomer = picked?.value === 'tell_customer';
  const noteRequired = rejectionChoices.length === 0 || picked?.value === 'other' || forCustomer;
  const canReject =
    (rejectionChoices.length === 0 || picked !== null) && (!noteRequired || reason.trim().length > 0);
  const [handledNote, setHandledNote] = useState('');
  // UltraCart's Refund Reason, pre-filled with Zoye's choice and changeable
  // before Execute (Deivydas 2026-10-12).
  const [refundReason, setRefundReason] = useState(row.refundReason ?? 'No Reason Provided');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const settle = (message: string) => {
    setOutcome(message);
    setProblem(null);
    onDone();
  };
  const fail = (error: unknown) => {
    setProblem(error instanceof ApiError ? error.message : 'That did not go through.');
  };

  const approve = useMutation({
    mutationFn: () =>
      apiPost<{ explanation: string; reallyHappened: boolean; customer?: { done: boolean; detail: string } }>(
        `/api/approvals/${row.id}/approve`,
        row.amountCents !== null ? { refundReason } : {},
      ),
    // The server's own explanation is shown verbatim rather than a cheerful
    // local string: it is the only thing that knows whether anything happened.
    // A refund also says what the customer was told (2026-10-22).
    onSuccess: (result) =>
      settle(result.customer ? `${result.explanation} Customer: ${result.customer.detail}.` : result.explanation),
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: () =>
      apiPost<{ customer?: { done: boolean; detail: string } }>(`/api/approvals/${row.id}/reject`, {
        reason: reason.trim(),
        ...(picked ? { rejection: picked.value } : {}),
      }),
    onSuccess: (result) =>
      settle(
        result.customer
          ? `Rejected, with your reason on the record. Customer: ${result.customer.detail}.`
          : 'Rejected, with your reason on the record.',
      ),
    onError: fail,
  });

  const markHandled = useMutation({
    mutationFn: () => apiPost(`/api/approvals/${row.id}/handled`, { note: handledNote.trim() }),
    onSuccess: () => settle('Marked handled. It leaves this list; "Show handled" brings it back.'),
    onError: fail,
  });

  const busy = approve.isPending || reject.isPending || markHandled.isPending;
  const canMarkHandled = (row.status === 'failed' || row.status === 'blocked') && row.handled === null;

  return (
    <>
      <tr className="align-top">
        <Td>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="text-left font-medium text-ink-900 hover:text-accent-700"
          >
            {row.typeLabel}
          </button>
          <div className="text-2xs text-ink-400">
            {/* The amount lives in the line, bold, since only refunds have
                one (Deivydas 2026-10-12: the column was "—" on every other row). */}
            {row.amountCents !== null ? (
              <span className="tabular font-semibold text-ink-900">{formatUsd(row.amountCents)} · </span>
            ) : null}
            {row.detail}
          </div>
          {row.firstStep ? <div className="mt-1 text-2xs font-medium text-hold-700">{row.firstStep}</div> : null}
        </Td>
        <Td>
          <div className="text-ink-700">{row.customer ?? 'Unknown sender'}</div>
          <Link
            to={`/tickets/${row.ticketId}`}
            className="font-mono text-2xs text-ink-400 hover:text-accent-700"
          >
            {row.ticketRef}
          </Link>
        </Td>
        <Td className="text-ink-700">{row.brand}</Td>
        <Td>
          <OrderCell row={row} />
        </Td>
        {showTags ? (
          <Td className="min-w-[220px]">
            <OrderTagsCell tags={row.orderTags} expected={row.tagsExpected} />
          </Td>
        ) : null}
        <Td>
          <Badge tone={STATUS_TONE[row.status]}>{row.status.replace(/_/g, ' ')}</Badge>
          {row.approvedBy ? (
            <div className="mt-0.5 text-2xs text-ink-400">by {row.approvedBy}</div>
          ) : null}
          {row.handled ? (
            <div className="mt-0.5 text-2xs text-ok-700">
              handled{row.handled.by ? ` by ${row.handled.by}` : ''} · {formatRelative(row.handled.at)}
            </div>
          ) : null}
        </Td>
        <Td className="text-xs text-ink-500" title={new Date(row.at).toISOString()}>
          {formatRelative(row.at)}
        </Td>
        <Td className="text-right">
          {row.approvable || canMarkHandled ? (
            <Button size="sm" variant="ghost" onClick={onToggle}>
              Review
            </Button>
          ) : row.handled ? (
            <span className="text-2xs text-ok-700">Handled</span>
          ) : (
            <span className="text-2xs text-ink-400">
              {row.instead ? 'Needs a person' : 'Settled'}
            </span>
          )}
        </Td>
      </tr>

      {open ? (
        <tr>
          <Td colSpan={showTags ? 8 : 7} className="bg-surface-sunken">
            <div className="grid gap-4 py-2 lg:grid-cols-[1.3fr_1fr]">
              <div>
                <div className="eyebrow mb-1">Why this amount</div>
                {row.policy.length > 0 ? (
                  // The deterministic layer's derivation, so approving is
                  // reading rather than trusting.
                  <ul className="mb-3 space-y-0.5 text-sm text-ink-700">
                    {row.policy.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mb-3 text-sm text-ink-400">
                    No policy decision recorded on this run.
                  </p>
                )}

                <div className="eyebrow mb-1">Draft reply</div>
                <p className="whitespace-pre-wrap text-sm text-ink-700">
                  {row.draftReply
                    ? withBillingLinkStandIn(row.draftReply)
                    : 'No draft was recorded on this run.'}
                </p>

                {row.blockedReasons.length > 0 ? (
                  <div className="mt-3 rounded border border-line bg-stop-100 px-3 py-2">
                    <div className="eyebrow mb-1 text-stop-700">Why the gate refused</div>
                    <ul className="list-disc space-y-0.5 pl-4 text-xs text-stop-700">
                      {row.blockedReasons.map((text) => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <div>
                {row.instead ? (
                  <div className="rounded border border-line bg-hold-100 px-3 py-2.5">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-hold-700">
                      <Wrench size={13} strokeWidth={1.75} aria-hidden />
                      Do this by hand
                    </div>
                    <p className="text-xs text-hold-700">{row.instead}</p>
                  </div>
                ) : row.approvable ? (
                  <>
                    <p className="mb-2 text-xs text-ink-500">{approveConsequence(row, dryRun)}</p>
                    {row.amountCents !== null && refundReasons.length > 0 ? (
                      <div className="mb-2">
                        <label htmlFor={`refund-reason-${row.id}`} className="eyebrow mb-1 block text-ink-500">
                          Refund reason in UltraCart
                        </label>
                        <select
                          id={`refund-reason-${row.id}`}
                          value={refundReason}
                          onChange={(event) => setRefundReason(event.target.value)}
                          className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900"
                        >
                          {(refundReasons.includes(refundReason) ? refundReasons : [refundReason, ...refundReasons]).map(
                            (value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    ) : null}
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => approve.mutate()}
                    >
                      {approveLabel(dryRun)}
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-ink-500">
                    {canMarkHandled
                      ? 'Zoye could not do this one. If a person has dealt with it, mark it handled below.'
                      : 'This one is already decided. Nothing here will change it.'}
                  </p>
                )}

                {/* Rejecting stays available even for a row nothing can execute
                    — it is how the by-hand path gets closed off with a note. */}
                {/* Pre-approved ('approved') too: since 2026-10-08 that is
                    where over-$200 refunds wait for a person, who must be
                    able to say no as well as yes. */}
                {row.status === 'awaiting_approval' ||
                row.status === 'proposed' ||
                row.status === 'approved' ? (
                  <div className="mt-4">
                    {rejectionChoices.length > 0 ? (
                      <>
                        <label htmlFor={`rejection-${row.id}`} className="eyebrow mb-1 block text-ink-500">
                          Reason for rejecting
                        </label>
                        <select
                          id={`rejection-${row.id}`}
                          value={rejection}
                          onChange={(event) => setRejection(event.target.value)}
                          className="mb-1.5 w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900"
                        >
                          <option value="">Pick a reason…</option>
                          {rejectionChoices.map((choice) => (
                            <option key={choice.value} value={choice.value}>
                              {choice.label}
                            </option>
                          ))}
                        </select>
                        {picked ? (
                          <p className="mb-1.5 text-xs text-ink-500">
                            {forCustomer
                              ? 'Zoye sends the customer what you write below, as written, inside a short greeting and sign-off, and leaves a note on the ticket.'
                              : picked.zoyeReplies
                              ? 'Zoye sends the customer the standard answer for this reason and leaves a note on the ticket.'
                              : 'The ticket comes to you in CS - Tier 2 with a note (or to the Tier 2 queue if Zendesk does not know you as a Tier 2 agent); you tell the customer.'}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    <label
                      htmlFor={`reason-${row.id}`}
                      className="eyebrow mb-1 block text-ink-500"
                    >
                      {forCustomer
                        ? 'What Zoye tells the customer (required)'
                        : rejectionChoices.length > 0
                          ? noteRequired
                            ? 'Note (required)'
                            : 'Note (optional)'
                          : 'Reason for rejecting'}
                    </label>
                    <textarea
                      id={`reason-${row.id}`}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      rows={forCustomer ? 4 : 2}
                      placeholder={
                        forCustomer
                          ? 'Written to the customer, e.g. "This order was already refunded in full on October 3."'
                          : noteRequired
                            ? 'Why not? This goes on the record.'
                            : 'Anything the record should add. The customer does not see it.'
                      }
                      className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900 placeholder:text-ink-400"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="mt-1.5"
                      // The API refuses an empty reason; disabling here means
                      // the operator learns that before the round trip.
                      disabled={busy || !canReject}
                      onClick={() => reject.mutate()}
                    >
                      Reject
                    </Button>
                  </div>
                ) : null}

                {/* A failed or blocked row a person dealt with by hand
                    (Deivydas 2026-10-14): it leaves the list. The status
                    stays, so the record still says the write did not happen. */}
                {canMarkHandled ? (
                  <div className="mt-4">
                    <label htmlFor={`handled-${row.id}`} className="eyebrow mb-1 block text-ink-500">
                      Dealt with it by hand?
                    </label>
                    <textarea
                      id={`handled-${row.id}`}
                      value={handledNote}
                      onChange={(event) => setHandledNote(event.target.value)}
                      rows={2}
                      placeholder="Optional: what was done (cancelled in ShipStation, refunded in UltraCart…)"
                      className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs text-ink-900 placeholder:text-ink-400"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1.5"
                      disabled={busy}
                      onClick={() => markHandled.mutate()}
                    >
                      Mark handled
                    </Button>
                  </div>
                ) : null}

                {outcome ? (
                  <p className="mt-3 flex items-start gap-1.5 text-xs text-ok-700">
                    <CheckCircle2 size={13} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
                    {outcome}
                  </p>
                ) : null}
                {problem ? <p className="mt-3 text-xs text-stop-700">{problem}</p> : null}
              </div>
            </div>
          </Td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The order the action and its tags are on, with the ticket's intent under
 * it. An action on one order whose tags target another is flagged: that is
 * how a wrong-account stamp (#1921122) would have shown up here.
 */
function OrderCell({ row }: { row: ApprovalRow }) {
  const tagsOrder = row.orderTags?.orderId ?? null;
  const orderId = row.orderId ?? tagsOrder;
  const mismatch = row.orderId !== null && tagsOrder !== null && row.orderId !== tagsOrder;
  return (
    <>
      <div className="font-mono text-xs text-ink-700">{orderId ?? <span className="text-ink-300">—</span>}</div>
      {mismatch ? <div className="text-2xs text-stop-700">tags go on {tagsOrder}</div> : null}
      <div className="text-2xs text-ink-400">{intentLabel(row.intent)}</div>
    </>
  );
}

/** "in 14h 20m", "due now" — the time left before a pending stamp writes. */
function untilLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const minutes = Math.round(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `in ${hours}h ${minutes % 60}m` : `in ${minutes}m`;
}

function OrderTagsCell({ tags, expected }: { tags: OrderTags | null; expected: boolean }) {
  if (!tags) return <span className="text-ink-300">—</span>;
  const properties = Object.entries(tags.properties);
  const list =
    properties.length > 0 ? (
      <ul className="mt-0.5 space-y-px text-2xs text-ink-500">
        {properties.map(([name, value]) => (
          <li key={name}>
            {name}: <span className="text-ink-700">{value}</span>
          </li>
        ))}
      </ul>
    ) : null;
  const note = (text: string | null) => (text ? <div className="mt-0.5 text-2xs text-ink-400">{text}</div> : null);

  switch (tags.state) {
    case 'written':
      return (
        <>
          <Badge tone="fact">written</Badge>
          {tags.at ? <span className="ml-1.5 text-2xs text-ink-400">{formatRelative(tags.at)}</span> : null}
          {list}
        </>
      );
    case 'unconfirmed':
    case 'dry_run':
      return (
        <>
          <Badge tone="held">{tags.state === 'dry_run' ? 'dry run' : 'unconfirmed'}</Badge>
          {list}
          {note(tags.detail)}
        </>
      );
    case 'failed':
      return (
        <>
          <Badge tone="stop">failed</Badge>
          {list}
          {note(tags.detail)}
        </>
      );
    case 'pending':
      return (
        <>
          <Badge tone="info">{tags.dueAt ? `writes ${untilLabel(tags.dueAt)}` : 'pending'}</Badge>
          {list}
          {note(tags.detail)}
        </>
      );
    case 'skipped':
      return (
        <>
          <Badge tone={expected ? 'stop' : 'neutral'}>skipped</Badge>
          {note(tags.detail)}
        </>
      );
    case 'person':
      return (
        <>
          <Badge tone="neutral">person's to stamp</Badge>
          {list}
        </>
      );
    case 'none':
      // The miss this column exists to catch: an action that changed what
      // the customer has or paid, and a conversation that planned no tags.
      return expected ? (
        <>
          <Badge tone="stop">nothing planned</Badge>
          {note(tags.detail)}
        </>
      ) : (
        <>
          <span className="text-2xs text-ink-400">none planned</span>
          {note(tags.detail)}
        </>
      );
  }
}

function DryRunBanner() {
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-hold-100 px-4 py-3">
      <ShieldAlert size={16} strokeWidth={1.75} className="mt-px shrink-0 text-hold-700" aria-hidden />
      <div className="text-xs text-hold-700">
        <span className="font-semibold">Dry run is on.</span> Approving records your decision and
        sends nothing to UltraCart. Approved actions stay in the queue marked{' '}
        <span className="font-medium">approved</span>, never <span className="font-medium">confirmed</span>
        , because a dry run cannot have confirmed anything real. Set{' '}
        <code className="font-mono">WRITE_ACTIONS_DRY_RUN=false</code> when you want writes to land.
      </div>
    </div>
  );
}

function TikTokBanner() {
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-hold-100 px-4 py-3">
      <MessageCircle size={16} strokeWidth={1.75} className="mt-px shrink-0 text-hold-700" aria-hidden />
      <div className="text-xs text-hold-700">
        <span className="font-semibold">TikTok Shop replies wait for a person.</span> Zoye drafts
        each answer; nothing reaches the customer until someone presses Send or Edit &amp; send, and
        the reply then appears in their TikTok chat. Hand off gives the chat to the CS group. A
        Zendesk status other than New or Open means a person may already have answered there.
      </div>
    </div>
  );
}

function LiveBanner() {
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-line bg-stop-100 px-4 py-3">
      <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0 text-stop-700" aria-hidden />
      <div className="text-xs text-stop-700">
        <span className="font-semibold">Writes are live.</span> Approving sends the action to
        UltraCart immediately. Refunds are not reversible from this console.
      </div>
    </div>
  );
}

function EmptyQueue({ status }: { status: string }) {
  const waiting = status === 'awaiting_approval';
  const awaitingCustomer = status === AWAITING_CUSTOMER_TAB;
  return (
    <Card>
      <CardHeader title="Nothing here" />
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-ink-700">
          {waiting
            ? 'No actions are waiting on a decision.'
            : awaitingCustomer
              ? 'No refunds are waiting on a customer’s yes.'
              : 'No actions with that status.'}
        </p>
        {awaitingCustomer ? (
          <p className="mx-auto mt-2 max-w-[560px] text-sm text-ink-400">
            Each night Zoye re-checks Blocked by policy. A refund held only because the customer has not said
            yes yet moves here; once they answer, the next night applies or clears it.
          </p>
        ) : null}
        {waiting ? (
          <p className="mx-auto mt-2 max-w-[560px] text-sm text-ink-400">
            In auto mode Zoye executes subscription changes, order cancellations, address
            changes, and refunds and replacements up to $200 herself — those appear under Done.
            Anything over $200 lands in Pre-approved for a person to Execute; anything the policy
            holds for other reasons waits here.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
